<template>
<lidar-chart
  @lidar-chart-ready="onLidarChartReady"
  @lidar-chart-destroy="onLidarChartDestroy"
/>
</template>

<script>
import LidarChart from '../Charts/LidarChart/index'
import {LIDAR_DATA_APPEND, LIDAR_DATA_INSERT, LIDAR_DATA_LIST_CHANGED, LIDAR_DATA_SELECT} from './EventKeys'
import {LIDAR_CHART_DESTROY, LIDAR_CHART_READY} from '../Charts/LidarChart/EventKeys'
import completeData from './completeData'

export default {
  name: 'LidarChartWrapper',
  components: {LidarChart},
  methods: {
    onLidarChartReady (lidarChart) {
      this.lidarChart = lidarChart
      this.$emit(LIDAR_CHART_READY, lidarChart)
      lidarChart.$on(LIDAR_DATA_LIST_CHANGED, this[LIDAR_DATA_LIST_CHANGED])
      lidarChart.$on(LIDAR_DATA_SELECT, this[LIDAR_DATA_SELECT])
      lidarChart.$on(LIDAR_DATA_INSERT, this[LIDAR_DATA_INSERT])
      lidarChart.$on(LIDAR_DATA_APPEND, this[LIDAR_DATA_APPEND])
    },
    onLidarChartDestroy () {
      let {lidarChart} = this

      lidarChart.$off(LIDAR_DATA_LIST_CHANGED, this[LIDAR_DATA_LIST_CHANGED])
      lidarChart.$off(LIDAR_DATA_SELECT, this[LIDAR_DATA_SELECT])
      lidarChart.$off(LIDAR_DATA_INSERT, this[LIDAR_DATA_INSERT])
      lidarChart.$off(LIDAR_DATA_APPEND, this[LIDAR_DATA_APPEND])
      this.$emit(LIDAR_CHART_DESTROY, lidarChart)

      this.lidarChart = null
    },
    [LIDAR_DATA_LIST_CHANGED] (dataOpts) {
      dataOpts = this.dataOpts = {...dataOpts}
      this.completeValues(dataOpts)
      this.reverseHeights(dataOpts)
      this.reverseValues(dataOpts)
      this.setChartOpts(dataOpts)
    },
    [LIDAR_DATA_SELECT] (dataTime) {
      let {dataOpts} = this

      if (!dataOpts) {
        return
      }

      let {heightStart, heightEnd} = this
      let xIndex = dataOpts.values.findIndex(value => value.dataTime === dataTime)
      let yIndex = Math.round((heightEnd + heightStart) / 2) - heightStart
      this.lidarChart.selectData(xIndex, yIndex)
    },
    [LIDAR_DATA_INSERT] (dataOpts) {
      let currentDataOpts = this.dataOpts
      dataOpts = {...dataOpts}

      currentDataOpts.beginTime = dataOpts.beginTime
      dataOpts.dataFrequency = currentDataOpts.dataFrequency

      this.completeValues(dataOpts)
      this.reverseValues(dataOpts)

      let {values} = dataOpts
      currentDataOpts.values = values.concat(currentDataOpts.values)

      let {heightStart, heightEnd} = this
      let chartOpts = {
        xAxis: {
          data: values.map(({dataTime}) => new Date(dataTime))
        },
        series: {
          data: values.map(({value}) => Array.isArray(value) ? value.slice(heightStart, heightEnd) : null)
        }
      }

      this.lidarChart.insertData(chartOpts)
    },
    [LIDAR_DATA_APPEND] (dataOpts) {
      let currentDataOpts = this.dataOpts
      dataOpts = {...dataOpts}

      currentDataOpts.endTime = dataOpts.endTime
      dataOpts.dataFrequency = currentDataOpts.dataFrequency

      this.completeValues(dataOpts)
      this.reverseValues(dataOpts)

      let {values} = dataOpts
      currentDataOpts.values = currentDataOpts.values.concat(values)

      let {heightStart, heightEnd} = this
      let chartOpts = {
        xAxis: {
          data: values.map(({dataTime}) => new Date(dataTime))
        },
        series: {
          data: values.map(({value}) => Array.isArray(value) ? value.slice(heightStart, heightEnd) : null)
        }
      }

      this.lidarChart.appendData(chartOpts)
    },
    completeValues (dataOpts) {
      dataOpts.values = completeData(dataOpts.beginTime, dataOpts.endTime, dataOpts.dataFrequency, dataOpts.values, dataTimeStr => ({
        id: null,
        dataTime: dataTimeStr,
        dasFlag: null,
        value: null
      }), 0)
    },
    reverseHeights (dataOpts) {
      dataOpts.heights = [...dataOpts.heights].reverse()
    },
    reverseValues (dataOpts) {
      dataOpts.values = dataOpts.values.map(value => {
        let val = value.value

        if (Array.isArray(val)) {
          val = [...val].reverse()
        }

        return {
          ...value,
          value: val
        }
      })
    },
    setChartOpts (dataOpts) {
      let {values} = dataOpts
      let {showHeights, showValues} = this.getDataRange(dataOpts)

      let chartOpts = {
        dataName: dataOpts.name,
        tooltip: {
          xAxisFormatter: value => value ? value.format('YYYY-MM-DD HH:mm') : '',
          yAxisFormatter: value => value,
          valueFormatter: value => value
        },
        legend: {
          minValue: dataOpts.legendMinVal,
          maxValue: dataOpts.legendMaxVal,
          colors: dataOpts.colors
        },
        xAxis: {
          name: '时间',
          unit: '',
          fit: true,
          interval: 48,
          data: values.map(({dataTime}) => new Date(dataTime)),
          formatter: dataTime => dataTime.format('DD日HH时')
        },
        yAxis: {
          name: dataOpts.yAxisName || '高度',
          unit: dataOpts.yAxisUnit || '米',
          fit: true,
          interval: 10,
          data: showHeights,
          formatter: height => height
        },
        series: {
          name: '监测值',
          unit: '',
          data: showValues,
          formatter: value => value
        }
      }

      this.lidarChart.setOption(chartOpts, true)
    },
    getDataRange ({heights, values, minHeight, maxHeight}) {
      let [heightStart, heightEnd] = this.getHeightRange(minHeight, maxHeight, heights)
      let showHeights = heights.slice(heightStart, heightEnd)
      this.heightStart = heightStart
      this.heightEnd = heightEnd === -1 ? showHeights.length : heightEnd

      return {
        heightStart,
        heightEnd,
        showHeights,
        showValues: values.map(({value}) => Array.isArray(value) ? value.slice(heightStart, heightEnd) : null)
      }
    },
    getHeightRange (minHeight, maxHeight, heights) {
      let heightEnd = heights.findIndex(height => height < minHeight)
      heightEnd = heightEnd === -1 ? heights.length : heightEnd
      return [
        heights.findIndex(height => height <= maxHeight),
        heightEnd
      ]
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/Data/ChartWrapper/LidarChartWrapper.vue